import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';

export type ChartType = 'line' | 'bar' | 'area' | 'pie';

export interface ChartSeries {
  dataKey: string;
  color: string;
  name?: string;
}

export interface ChartWidgetProps {
  title: string;
  type: ChartType;
  data: Record<string, unknown>[];
  xKey?: string;
  series: ChartSeries[];
  height?: number;
  showGrid?: boolean;
  showLegend?: boolean;
}

const TOOLTIP_STYLE = {
  background: '#1a2744',
  border: '1px solid #2d4a6f',
  borderRadius: '8px',
  fontSize: '12px',
};

export default function ChartWidget({
  title,
  type,
  data,
  xKey = 'name',
  series,
  height = 220,
  showGrid = true,
}: ChartWidgetProps) {
  const renderChart = () => {
    switch (type) {
      case 'line':
        return (
          <LineChart data={data}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />}
            <XAxis dataKey={xKey} tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} />
            <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#94a3b8' }} />
            {series.map((s) => (
              <Line
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                stroke={s.color}
                strokeWidth={2}
                dot={false}
                name={s.name ?? s.dataKey}
              />
            ))}
          </LineChart>
        );

      case 'bar':
        return (
          <BarChart data={data}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />}
            <XAxis dataKey={xKey} tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} />
            <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#94a3b8' }} />
            {series.map((s) => (
              <Bar key={s.dataKey} dataKey={s.dataKey} fill={s.color} radius={[4, 4, 0, 0]} name={s.name ?? s.dataKey} />
            ))}
          </BarChart>
        );

      case 'area':
        return (
          <AreaChart data={data}>
            <defs>
              {series.map((s) => (
                <linearGradient key={`grad-${s.dataKey}`} id={`grad-${s.dataKey}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={s.color} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={s.color} stopOpacity={0} />
                </linearGradient>
              ))}
            </defs>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" />}
            <XAxis dataKey={xKey} tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} />
            <YAxis tick={{ fill: '#64748b', fontSize: 10 }} tickLine={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={{ color: '#94a3b8' }} />
            {series.map((s) => (
              <Area
                key={s.dataKey}
                type="monotone"
                dataKey={s.dataKey}
                stroke={s.color}
                fill={`url(#grad-${s.dataKey})`}
                strokeWidth={2}
                name={s.name ?? s.dataKey}
              />
            ))}
          </AreaChart>
        );

      case 'pie':
        return (
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={70}
              paddingAngle={3}
              dataKey={series[0]?.dataKey ?? 'value'}
            >
              {data.map((entry, index) => (
                <Cell
                  key={index}
                  fill={(entry as Record<string, string>).color ?? series[index % series.length]?.color ?? '#64748b'}
                />
              ))}
            </Pie>
            <Tooltip contentStyle={TOOLTIP_STYLE} />
          </PieChart>
        );

      default:
        return null;
    }
  };

  return (
    <div className="glass-panel p-4 h-full flex flex-col">
      <h3 className="text-sm font-semibold text-gray-300 mb-3">{title}</h3>
      <div className="flex-1 min-h-0">
        <ResponsiveContainer width="100%" height={height}>
          {renderChart() ?? <div />}
        </ResponsiveContainer>
      </div>
    </div>
  );
}
